import React, { useState, useEffect, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { useQuery } from "@apollo/client/react";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { KIT_ASSETS_QUERY } from "src/constants/graphql/queries";
import ItemId from "src/views/shopping/Orders/ItemId";
import ScanOutModal from "../ScanOutModal";
import useAuth from "src/hooks/useAuth";
import useMarkStatusAs from "../hooks/useMarkStatusAs";

export default function ManualLocationKitAssets({ assetId, rackLocations, item, onComplete, orderType = "inventoryRequest" }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const { loading, data: { asset } = {} } = useQuery(KIT_ASSETS_QUERY, {
    variables: { id: assetId },
    fetchPolicy: "cache-first",
  });
  const [openScanModal, setOpenScanModal] = useState(false);
  const [pickedItems, setPickedItems] = useState({});
  const [pickedStatus, setPickedStatus] = useState(false);
  const { markAs, loading: mutationLoading } = useMarkStatusAs(orderType);
  const mutationCalled = useRef(false);

  const getRackLocation = kitAssetId => {
    const locations = rackLocations
      .filter(rl => String(rl.asset_id) === String(kitAssetId))
      .flatMap(rl => {
        const locationNames = (rl.warehouse_locations || [])
          .map(loc => loc.location_name)
          .filter(Boolean);
        return locationNames.map(name => `${name} (${rl.sub_qty})`);
      })
      .filter(Boolean);
    return locations.length > 0 ? locations.join("\n") : "No specific location";
  };

  const getTotalQuantity = kitAssetId => {
    return rackLocations
      .filter(rl => String(rl.asset_id) === String(kitAssetId))
      .reduce((total, location) => total + location.sub_qty, 0);
  };

  // Determine which assets to show based on group_by_sku_number setting
  const sortedAssets = (() => {
    if (asset?.groupBySkuNumber && item?.kitInventoryItems?.length > 0) {
      // For group_by_sku_number = true: Use kit inventory items (FIFO allocated assets)
      return item.kitInventoryItems
        .map(kitItem => ({
          ...kitItem.asset,
          quantity: kitItem.quantity,
        }))
        .sort((a, b) => getRackLocation(a.id).localeCompare(getRackLocation(b.id)));
    } else {
      // For group_by_sku_number = false: Use original kit assets logic
      return (asset?.kitAssets || [])
        .map(v => ({ ...v.asset, quantity: getTotalQuantity(v.asset.id) }))
        .sort((a, b) => getRackLocation(a.id).localeCompare(getRackLocation(b.id)));
    }
  })();

  useEffect(() => {
    if (
      sortedAssets.length &&
      Object.keys(pickedItems).length === sortedAssets.length &&
      !pickedStatus &&
      !mutationLoading &&
      !mutationCalled.current
    ) {
      const allPicked = sortedAssets.every(({ id }) => pickedItems[id]);
      if (allPicked) {
        mutationCalled.current = true;
        markAs(item).then(() => {
          setPickedStatus(true);
          onComplete && onComplete();
        });
      }
    }
  }, [pickedItems, sortedAssets, item, markAs, pickedStatus, mutationLoading, onComplete]);

  const handleMarkAsPicked = kitAssetId => {
    setPickedItems(prev => ({ ...prev, [kitAssetId]: true }));
  };

  const handleScanComplete = kitAssetId => {
    setPickedItems(prev => ({ ...prev, [kitAssetId]: true }));
    setOpenScanModal(false);
  };

  return loading ? (
    <CircularProgress sx={{ ml: 1 }} size={22} />
  ) : (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell align="center">Image</TableCell>
          <TableCell>Item ID</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>Quantity Requested</TableCell>
          <TableCell>Pick Location(s)</TableCell>
          <TableCell align="right" width={160}>
            {orderType === "internalWorkOrder" ? "Stage Status" : "Pick Status"}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedAssets.map((asset, index) => (
          <TableRow key={index}>
            <TableCell align="center">
              {asset.images && asset.images[0]?.url ? (
                <img
                  src={asset.images[0].url}
                  alt={asset.description}
                  style={{ maxWidth: 90, maxHeight: 60 }}
                />
              ) : (
                "No image"
              )}
            </TableCell>
            <TableCell>
              <ItemId asset={asset} />
            </TableCell>
            <TableCell>{asset.description}</TableCell>
            <TableCell>{asset.quantity}</TableCell>
            <TableCell>
              {getRackLocation(asset.id)
                .split("\n")
                .map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
            </TableCell>
            <TableCell align="right">
              {pickedItems[asset.id] ? (
                <Button
                  sx={{
                    color: "green",
                    textTransform: "capitalize",
                    "&.Mui-disabled": { color: "green" },
                  }}
                  disabled
                >
                  {orderType === "internalWorkOrder" ? "Staged" : "Picked"}
                </Button>
              ) : settings.scan_out_modal ? (
                <Button
                  startIcon={loading && <CircularProgress size={22} />}
                  onClick={() => setOpenScanModal(true)}
                  sx={{
                    color: "red",
                    textTransform: "capitalize",
                    "&.Mui-disabled": { color: "green" },
                  }}
                >
                  Scan Out
                </Button>
              ) : (
                <Button
                  startIcon={loading && <CircularProgress size={22} />}
                  onClick={() => handleMarkAsPicked(asset.id)}
                  sx={{
                    color: "red",
                    textTransform: "capitalize",
                    "&.Mui-disabled": { color: "green" },
                  }}
                >
                  {orderType === "internalWorkOrder" ? "Mark as Staged" : "Mark as Picked"}
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      {openScanModal && (
        <ScanOutModal
          asset={asset}
          itemId={assetId}
          onClose={() => setOpenScanModal(false)}
          onScanComplete={() => handleScanComplete(assetId)}
        />
      )}
    </Table>
  );
}
